/**
 * ehr#743 (PROFILE_TABS_SEARCH_10-30-V15): the client chart's Profile section tabs. One strip,
 * Mock B (a white bordered strip, short dividers, the open tab a solid green pill). It is not a
 * wizard: no numbers, checks or Next, and any tab opens in any order. An amber dot after a label
 * means something on that section is missing; it follows the chart live.
 *
 * Order (V15): Contact Info, Demographics, Family & Contacts, Emergency Contact, Care Team,
 * Insurance / Payor, Authorizations, Guarantor, Referral, then More, which holds Pharmacy and
 * Employment. Tabs added later go under More (V15: "Tabs added in the future go under More").
 */
import { preferredContactBlock, referralNpiValid, type PreferredContactGap } from '@ehr/shared';
import { emailValid, phoneValid, ssnFieldValid, zipValid } from './contactFields.js';
import { referralOtherMissing } from './referralOther.js';
import { contactSaveMissing } from './requiredContact.js';

/** The Authorizations section: the chart's authorizations (no Profile fields of its own). */
export const AUTH_SECTION = 'authorizations';
export const AUTH_LABEL = 'Authorizations';

/** The strip's own tabs, in V15's order. Everything else goes under More. */
export const PROFILE_ROW = [
  'contact',
  'identity',
  'guardian',
  'emergency',
  'careteam',
  'insurance',
  AUTH_SECTION,
  'guarantor',
  'referral',
] as const;

/** More's tabs first, in V15's order (35a, 36b); a section on neither list is added after them. */
export const PROFILE_MORE = ['pharmacy', 'employment'] as const;

export type ProfileTab = { key: string; label: string };

/**
 * The strip (`row`) and the More list (`more`) for the chart's sections. Authorizations shows only
 * when `withAuth` (the old top tab's rule: client charts; the PHI gate is the Profile's own).
 */
export function profileTabs(
  sections: readonly ProfileTab[],
  withAuth = true,
): { row: ProfileTab[]; more: ProfileTab[] } {
  const byKey = new Map(sections.map((s) => [s.key, s] as const));
  const row: ProfileTab[] = [];
  for (const k of PROFILE_ROW) {
    if (k === AUTH_SECTION) {
      if (withAuth) row.push({ key: AUTH_SECTION, label: AUTH_LABEL });
      continue;
    }
    const s = byKey.get(k);
    if (s) row.push({ key: s.key, label: s.label });
  }
  const placed = new Set<string>(row.map((t) => t.key));
  const more: ProfileTab[] = [];
  for (const k of PROFILE_MORE) {
    const s = byKey.get(k);
    if (s) more.push({ key: s.key, label: s.label });
    placed.add(k);
  }
  for (const s of sections) if (!placed.has(s.key)) more.push({ key: s.key, label: s.label });
  return { row, more };
}

/**
 * Where the chart opens for the tab a link names. Retired top tabs land where their content went:
 * Treatment Plans on Clinical Notes, Screeners on Forms (ehr#640), and (ehr#743, V15) Authorizations
 * on Profile › Authorizations, so old links and saved views keep working. No tab named: a client
 * opens on Summary (ehr#230), anyone else on Contact Notes.
 */
export function startView(
  initialView: string | undefined,
  isClient: boolean,
): { view: string; section: string } {
  const section = initialView === AUTH_SECTION ? AUTH_SECTION : 'contact';
  if (initialView === AUTH_SECTION) return { view: 'demographics', section };
  if (initialView === 'treatment_plans') return { view: 'clinical_notes', section };
  if (initialView === 'screeners') return { view: 'forms', section };
  return { view: initialView ?? (isClient ? 'summary' : 'notes'), section };
}

/** Every tab in screen order (the strip, then More): the phone dropdown and the search order. */
export function profileTabOrder(tabs: { row: ProfileTab[]; more: ProfileTab[] }): ProfileTab[] {
  return [...tabs.row, ...tabs.more];
}

/** More's name: "More", or the open tab's name while one of its tabs is open ("Employment"). */
export function moreLabel(more: readonly ProfileTab[], current: string): string {
  return more.find((t) => t.key === current)?.label ?? 'More';
}

/**
 * Where an arrow key moves focus along a list of `count` items (V15: Right / Down forward, Left /
 * Up back, wrapping at the ends). Other keys: null.
 */
export function arrowMove(key: string, index: number, count: number): number | null {
  if (count < 1) return null;
  if (key === 'ArrowRight' || key === 'ArrowDown') return (index + 1) % count;
  if (key === 'ArrowLeft' || key === 'ArrowUp') return (index - 1 + count) % count;
  return null;
}

/** The Profile fields Save record requires (blank blocks it; Save draft skips them). */
export const PROFILE_REQUIRED: { sec: string; field: string; label: string }[] = [
  { sec: 'contact', field: 'firstName', label: 'First name' },
  { sec: 'contact', field: 'lastName', label: 'Last name' },
  { sec: 'identity', field: 'dob', label: 'Date of birth' },
  { sec: 'identity', field: 'ssn', label: 'SSN' },
  // ehr#743 (V11): Referral source is required.
  { sec: 'referral', field: 'referralSource', label: 'Referral source' },
];

type Blob = Record<string, Record<string, string> | undefined>;
type CheckSection = {
  key: string;
  fields?: readonly { key: string; label: string; type?: string }[];
};
export type ProfileProblem = { sec: string; field: string; label: string };

/**
 * What a save would flag red, the one rule set for the save and the amber dots: blank required
 * fields (Save record only), values in the wrong format (either save: a part-typed SSN, an email,
 * phone, ZIP or Referring provider NPI that is not complete), and (ehr#661) a preferred contact
 * method picked with nothing to reach, or the one kept cleared (`stored` = Contact Info as saved).
 */
export function profileProblems(
  sections: readonly CheckSection[],
  data: Blob,
  opts: { draft: boolean; stored?: unknown },
): { missing: ProfileProblem[]; bad: ProfileProblem[]; prefer: PreferredContactGap | null } {
  const bad: ProfileProblem[] = [];
  // SSN must be a complete 9-digit number if filled, pointed out on either save.
  if (!ssnFieldValid(String(data.identity?.ssn ?? '')))
    bad.push({ sec: 'identity', field: 'ssn', label: 'SSN (9 digits)' });
  // #9: a badly formatted email / phone / ZIP / NPI on a filled field.
  for (const s of sections) {
    for (const f of s.fields ?? []) {
      const v = String(data[s.key]?.[f.key] ?? '');
      if (f.type === 'email' && !emailValid(v))
        bad.push({ sec: s.key, field: f.key, label: f.label });
      else if (f.type === 'tel' && !phoneValid(v))
        bad.push({ sec: s.key, field: f.key, label: f.label });
      else if (f.key === 'zip' && !zipValid(v))
        bad.push({ sec: s.key, field: f.key, label: f.label });
      else if (f.key === 'referringNpi' && !referralNpiValid(v))
        bad.push({ sec: s.key, field: f.key, label: `${f.label} (10 digits)` });
    }
  }
  const required = opts.draft
    ? []
    : PROFILE_REQUIRED.filter((t) => !(data[t.sec]?.[t.field] ?? '').trim());
  // ehr#733 / #734: Mobile phone, and a Home / Work phone with a box ticked, on Save record only
  // (listed after the names, the way the form reads).
  const missing = [
    ...required.filter((t) => t.sec === 'contact'),
    ...contactSaveMissing(data.contact, opts.draft),
    ...required.filter((t) => t.sec !== 'contact'),
    // ehr#758: "Other" as Referral source needs its "Please specify" box (Save record only).
    ...referralOtherMissing(data.referral, opts.draft),
  ];
  const prefer = preferredContactBlock(opts.stored, data.contact);
  return { missing, bad, prefer };
}

/**
 * The sections that get the amber dot: anything Save record would flag red there (blank required
 * field, wrong format, the preferred contact gap), or a section the chart already flagged as
 * missing when it is empty: every section but the optional ones, and Family & Contacts only for a
 * minor (the old section pills' orange rule, mockup .tab-empty).
 */
export function sectionsNeedingInfo(
  sections: readonly CheckSection[],
  data: Blob,
  opts: { stored?: unknown; isMinor: boolean; optional: ReadonlySet<string> },
): Set<string> {
  const { missing, bad, prefer } = profileProblems(sections, data, {
    draft: false,
    stored: opts.stored,
  });
  const need = new Set<string>([...missing, ...bad].map((p) => p.sec));
  if (prefer) need.add('contact');
  for (const s of sections) {
    if (opts.optional.has(s.key) || (s.key === 'guardian' && !opts.isMinor)) continue;
    const filled = Object.values(data[s.key] ?? {}).some((v) => v && String(v).trim());
    if (!filled) need.add(s.key);
  }
  return need;
}
